import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import {
  Purchase,
  UpdatePurchaseDto,
  PurchaseItem,
  PaymentMethod,
  PurchaseStatus,
  getPaymentMethodDisplay,
  calculatePurchaseTotal,
} from '../../api/purchases';
import { transactionsApi } from '../../api/transactions';
import { BrandModal, BrandModalBody, BrandModalFooter, BrandModalHeader } from '../ui/BrandModal';
import AmountInput from '../ui/AmountInput';

interface EditPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (data: UpdatePurchaseDto) => Promise<void>;
  purchase: Purchase | null;
}

const EditPurchaseModal: React.FC<EditPurchaseModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
  purchase,
}) => {
  const [formData, setFormData] = useState<Omit<UpdatePurchaseDto, 'items'>>({
    invoiceNumber: '',
    purchaseDate: '',
    supplierName: '',
    supplierPhone: '',
    supplierAddress: '',
    paymentMethod: PaymentMethod.CASH,
    total: 0,
    notes: '',
    status: PurchaseStatus.PENDING,
  });

  const [items, setItems] = useState<Omit<PurchaseItem, 'id'>[]>([]);
  const [loading, setLoading] = useState(false);

  // Initialize form data when purchase changes
  useEffect(() => {
    if (purchase) {
      setFormData({
        invoiceNumber: purchase.invoiceNumber,
        purchaseDate: purchase.purchaseDate.split('T')[0], // Convert to date format
        supplierName: purchase.supplierName,
        supplierPhone: purchase.supplierPhone,
        supplierAddress: purchase.supplierAddress,
        paymentMethod: purchase.paymentMethod,
        total: purchase.total,
        notes: purchase.notes || '',
        status: purchase.status,
      });

      setItems(purchase.items.map(item => ({
        productName: item.productName,
        quantity: Number(item.quantity) || 0,
        unitPrice: Number(item.unitPrice) || 0,
        totalPrice: Number(item.totalPrice) || 0,
      })));
    }
  }, [purchase]);

  const updateItemTotal = (index: number, quantity: number, unitPrice: number) => {
    const totalPrice = quantity * unitPrice;
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      quantity,
      unitPrice,
      totalPrice,
    };
    setItems(updatedItems);
    
    // Update total
    const newTotal = calculatePurchaseTotal(updatedItems);
    setFormData(prev => ({ ...prev, total: newTotal }));
  };

  const addItem = () => {
    setItems(prev => [...prev, {
      productName: '',
      quantity: 1,
      unitPrice: 0,
      totalPrice: 0,
    }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      const updatedItems = items.filter((_, i) => i !== index);
      setItems(updatedItems);
      
      // Update total
      const newTotal = calculatePurchaseTotal(updatedItems);
      setFormData(prev => ({ ...prev, total: newTotal }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loading) return;
    
    try {
      setLoading(true);
      
      const submitData: UpdatePurchaseDto = {
        ...formData,
        items: items.filter(item => item.productName.trim() !== ''),
      };

      await onUpdate(submitData);
    } catch (error) {
      console.error('Error updating purchase:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (loading) return;
    
    try {
      setLoading(true);
      await transactionsApi.approveTransaction(purchase.id);
      onClose();
    } catch (error) {
      console.error('Error approving purchase:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!purchase) return null;

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-4xl">
      <BrandModalHeader title={`Edit Purchase Order - ${purchase.invoiceNumber}`} onClose={onClose} />
      <form id="edit-purchase-form" onSubmit={handleSubmit}>
        <BrandModalBody>
              {purchase.status === PurchaseStatus.PENDING && (
                <div className="mb-4 flex justify-end">
                  <button
                    type="button"
                    onClick={handleApprove}
                    disabled={loading}
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-green-600 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    Approve
                  </button>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column - Basic Information */}
                <div className="space-y-4">
                  <div>
                    <label htmlFor="invoiceNumber" className="block text-sm font-medium text-slate-700">
                      Purchase Order Number
                    </label>
                    <input
                      type="text"
                      id="invoiceNumber"
                      value={formData.invoiceNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="purchaseDate" className="block text-sm font-medium text-slate-700">
                      Purchase Date
                    </label>
                    <input
                      type="date"
                      id="purchaseDate"
                      value={formData.purchaseDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, purchaseDate: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="paymentMethod" className="block text-sm font-medium text-slate-700">
                      Payment Method
                    </label>
                    <select
                      id="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value as PaymentMethod }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      required
                    >
                      {Object.values(PaymentMethod).map(method => (
                        <option key={method} value={method}>
                          {getPaymentMethodDisplay(method)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Right Column - Supplier Information */}
                <div className="space-y-4">
                  <div>
                    <label htmlFor="supplierName" className="block text-sm font-medium text-slate-700">
                      Supplier Name *
                    </label>
                    <input
                      type="text"
                      id="supplierName"
                      value={formData.supplierName}
                      onChange={(e) => setFormData(prev => ({ ...prev, supplierName: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="supplierPhone" className="block text-sm font-medium text-slate-700">
                      Supplier Phone
                    </label>
                    <input
                      type="tel"
                      id="supplierPhone"
                      value={formData.supplierPhone}
                      onChange={(e) => setFormData(prev => ({ ...prev, supplierPhone: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="supplierAddress" className="block text-sm font-medium text-slate-700">
                      Supplier Address
                    </label>
                    <textarea
                      id="supplierAddress"
                      value={formData.supplierAddress}
                      onChange={(e) => setFormData(prev => ({ ...prev, supplierAddress: e.target.value }))}
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Items Section */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-medium text-slate-900">Purchase Items</h4>
                  <button
                    type="button"
                    onClick={addItem}
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-cyan-600 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Item
                  </button>
                </div>

                <div className="space-y-3">
                  {items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-3 items-end">
                      <div className="col-span-5">
                        <label className="block text-sm font-medium text-slate-700">Product Name</label>
                        <input
                          type="text"
                          value={item.productName}
                          onChange={(e) => {
                            const updatedItems = [...items];
                            updatedItems[index].productName = e.target.value;
                            setItems(updatedItems);
                          }}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="Enter product name..."
                          required
                        />
                      </div>

                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-slate-700">Quantity</label>
                        <input
                          type="number"
                          min="1"
                          step="0.01"
                          value={item.quantity}
                          onChange={(e) => {
                            const quantity = parseFloat(e.target.value) || 1;
                            updateItemTotal(index, quantity, item.unitPrice);
                          }}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>

                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-slate-700">Unit Price</label>
                        <AmountInput
                          value={item.unitPrice}
                          onChange={(v) => updateItemTotal(index, item.quantity, v ?? 0)}
                          min={0}
                          inputClassName="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>

                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-slate-700">Total</label>
                        <input
                          type="number"
                          value={Number(item.totalPrice || 0).toFixed(2)}
                          readOnly
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-slate-50/80 sm:text-sm"
                        />
                      </div>

                      <div className="col-span-1">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          disabled={items.length === 1}
                          className="p-2 text-red-600 hover:text-red-800 disabled:text-slate-400 disabled:cursor-not-allowed"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Total Display */}
                <div className="mt-4 flex justify-end">
                  <div className="text-right">
                    <div className="text-sm text-slate-500">Total Amount</div>
                    <div className="text-xl font-bold text-slate-900">
                      {formData.total.toLocaleString('en-ZA', { 
                        style: 'currency', 
                        currency: 'ZAR',
                        minimumFractionDigits: 2 
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              <div className="mt-6">
                <label htmlFor="notes" className="block text-sm font-medium text-slate-700">
                  Notes
                </label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Additional notes or comments..."
                />
              </div>
        </BrandModalBody>

        <BrandModalFooter>
          <button type="button" onClick={onClose} className="btn btn-secondary text-sm">
            Cancel
          </button>
          <button
            type="submit"
            form="edit-purchase-form"
            disabled={loading || !formData.supplierName?.trim() || items.every(item => !item.productName.trim())}
            className="btn btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Updating...' : 'Update Purchase Order'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
};

export default EditPurchaseModal; 